import { useQuery, getTradovateConnections } from "wasp/client/operations";
import { anyTotalsArePartial } from "../../trading-accounts/tradovate/tradovateSyncBanner";

/**
 * L'utilisateur est-il sur le flux TEMPS REEL ?
 *
 * ── A QUOI CA SERT, ET POURQUOI CE N'EST PAS COSMETIQUE ────────────────────
 *
 * Ecrire un trade en base en quelques secondes ne sert A RIEN si l'interface
 * ne le relit qu'au rechargement de la page. Un betatesteur qui laisse son
 * journal ouvert verrait le meme delai qu'avant, et conclurait -- a tort --
 * que le temps reel ne marche pas.
 *
 * Ce hook dit aux ecrans QUAND il vaut la peine de se rafraichir souvent. Il
 * ne declenche rien par lui-meme : il rend un booleen que chaque ecran traduit
 * en cadence.
 *
 * ── POURQUOI CONDITIONNER PLUTOT QUE POLLER TOUT LE MONDE ─────────────────
 *
 * Un rafraichissement court impose a tous les utilisateurs multiplierait la
 * charge serveur pour un gain nul chez ceux qui restent en REST : leurs
 * donnees ne changent qu'une fois par cycle de 15 minutes. La cadence rapide
 * n'a de sens que la ou les donnees bougent vraiment.
 *
 * ⚠️ `realtimeStatus` vaut "not_enabled" pour l'immense majorite des
 * utilisateurs, et le serveur ne le calcule QUE si le socket alimente
 * reellement leurs donnees (cf. `shouldShowRealtimeStatus`). Ce hook herite
 * donc de ce verrou sans avoir a le redire.
 */
export function useRealtimeSync(): {
  /** Au moins une connexion livre-t-elle des donnees en temps reel ? */
  isLive: boolean;
  /**
   * Les totaux affiches sont-ils susceptibles d'etre INCOMPLETS ?
   *
   * Vrai pendant une sync initiale, ou apres un cycle bride par le quota. Les
   * trades sont ecrits un par un hors transaction : les ecrans qui somment des
   * trades doivent le dire au lieu d'afficher un chiffre faux comme un fait.
   */
  totalsArePartial: boolean;
} {
  // MEME QUERY que le badge de statut, donc MEME ENTREE DE CACHE react-query :
  // ce hook ne declenche aucune requete supplementaire, il lit ce que la page
  // des comptes a deja charge. Les arguments doivent rester identiques ({}) --
  // react-query indexe le cache sur les arguments, et `undefined` creerait une
  // seconde entree donc un second appel reseau.
  const { data: connections } = useQuery(getTradovateConnections, {});

  // « live » UNIQUEMENT, jamais "reconnecting" ni "interrupted" : pendant une
  // interruption, les donnees arrivent de nouveau par le REST toutes les 15
  // minutes, et garder une cadence rapide ne ferait que consommer du reseau
  // pour relire la meme chose.
  const isLive = (connections ?? []).some(
    (conn: any) => conn?.realtimeStatus === "live",
  );

  // Regle isolee dans un module pur (cf. tradovateSyncBanner.ts) : c'est elle
  // qui decide d'avertir sur des chiffres financiers, elle merite des tests.
  const totalsArePartial = anyTotalsArePartial(connections as any[]);

  return { isLive, totalsArePartial };
}

export default useRealtimeSync;
